import './customSpecialtyDrawer.scss';

import React, { useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { Window } from '../overlays/window';
import { WindowHeader } from '../overlays/windowHeader';
import { Button, SelectInput } from '../input';
import { CustomSpecialtyDrawerState } from './customSpecialtyDrawerState';

type Props = {
  model: CustomSpecialtyDrawerState;
};

const trainingDataRoleItems = [
  { id: 'Exemplar', value: 'Exemplar', label: 'Completed report example' },
  { id: 'Reference', value: 'Reference', label: 'Supporting reference (stored only)' },
];

export const CustomSpecialtyDrawer = observer(({ model }: Props) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    model.window.listen((msg) => {
      if (msg.type === 'close' || msg.type === 'outsideClick') {
        model.close();
      }
    });
  }, [model]);

  useEffect(() => {
    if (!model.selectedTrainingDataFile && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [model.selectedTrainingDataFile]);

  return (
    <Window
      model={model.window}
      visible={model.window.isVisible}
      className="custom-specialty-drawer"
      isLoading={model.isLoading}
      closeOnEscKey={true}>

      <WindowHeader
        model={model.window}
        title="Custom Specialty Details"
      />

      <div className="drawer-body">
        {model.error && (
          <div className="drawer-error" role="alert">{model.error}</div>
        )}

        {model.specialty && (
          <>
            <div className="drawer-section drawer-summary">
              <div className="drawer-summary-heading">
                <div>
                  <h4 className="drawer-section-title">Name</h4>
                  <p className="drawer-section-content drawer-specialty-name">{model.specialty.name}</p>
                </div>
                <span className={`drawer-status drawer-status-${model.specialty.status.toLowerCase()}`}>
                  {model.specialty.status}
                </span>
              </div>

              {model.specialty.description && (
                <div className="drawer-description">
                  <h4 className="drawer-section-title">Description</h4>
                  <p className="drawer-section-content">{model.specialty.description}</p>
                </div>
              )}
            </div>

            <div className="drawer-section">
              <label>
                <input
                  type="checkbox"
                  checked={model.specialty.duiReportCardEnabled ?? false}
                  disabled={model.isSavingReportCard || model.isRetraining || model.isReverting}
                  onChange={event => void model.setDuiReportCardEnabled(event.target.checked)}
                />
                {' '}Include DUI defense review card
              </label>
              <p className="training-data-help">
                Adds a preliminary review alongside the full report using the transcript and available video.
                Sampled video and timing ranges require investigator confirmation.
                Coverage gaps are listed. Applies to newly started reports.
              </p>
              {model.reportCardError && <p role="alert">{model.reportCardError}</p>}
            </div>

            <div className="drawer-section training-data-section">
              <h4 className="drawer-section-title">Training Data</h4>
              <div className="training-data-list">
                {model.trainingData.map(item => (
                  <div className="training-data-row" key={item.id}>
                    <div className="training-data-copy">
                      {item.downloadUrl
                        ? <a href={item.downloadUrl} target="_blank" rel="noreferrer" title={item.fileName}>{item.fileName}</a>
                        : <span title={item.fileName}>{item.fileName}</span>}
                      <span className="training-data-role-badge">{item.role}</span>
                    </div>
                    {!item.initial && (
                      <Button
                        className="link link-red small training-data-remove"
                        label={model.removingTrainingDataId === item.id ? 'Removing…' : 'Remove'}
                        aria-label={`Remove ${item.fileName}`}
                        disabled={model.removingTrainingDataId === item.id || model.isRetraining || model.isReverting}
                        onClick={() => void model.removeTrainingData(item.id)}
                      />
                    )}
                  </div>
                ))}
                {model.trainingData.length === 0 && (
                  <p className="training-data-empty">No training documents yet.</p>
                )}
              </div>

              <div className="training-data-add">
                <h5 className="training-data-add-title">Add training data</h5>
                <SelectInput
                  id="training-data-role"
                  className="training-data-role-input"
                  label="Document role"
                  aria-label="Training data role"
                  value={trainingDataRoleItems.find(item => item.value === model.trainingDataRole)}
                  items={trainingDataRoleItems}
                  showEmptyItem={false}
                  disabled={model.isUploadingTrainingData || model.isRetraining || model.isReverting}
                  onChange={event => model.setTrainingDataRole(event.target.value as 'Exemplar' | 'Reference')}
                />
                <input
                  ref={fileInputRef}
                  aria-label="Training data document"
                  className="training-data-file-input"
                  type="file"
                  accept=".pdf,.docx,.txt"
                  tabIndex={-1}
                  disabled={model.isUploadingTrainingData || model.isRetraining || model.isReverting}
                  onChange={event => model.setTrainingDataFile(event.target.files?.[0] ?? null)}
                />
                <div className="training-data-file-row">
                  <Button
                    className="secondary medium"
                    label="Choose document"
                    disabled={model.isUploadingTrainingData || model.isRetraining || model.isReverting}
                    onClick={() => fileInputRef.current?.click()}
                  />
                  <span
                    className="training-data-filename"
                    title={model.selectedTrainingDataFile?.name ?? 'No document selected'}>
                    {model.selectedTrainingDataFile?.name ?? 'No document selected'}
                  </span>
                </div>
                <Button
                  className="primary medium training-data-submit"
                  label={model.isUploadingTrainingData ? 'Uploading…' : 'Add training data'}
                  disabled={!model.selectedTrainingDataFile || model.isUploadingTrainingData
                    || model.isRetraining || model.isReverting}
                  onClick={() => void model.uploadTrainingData()}
                />
                <p className="training-data-help">PDF, DOCX, or TXT · 25 MB maximum</p>
                {model.trainingDataError && (
                  <p className="training-data-error" role="alert">{model.trainingDataError}</p>
                )}
              </div>

              <div className="training-data-file-row">
                <Button
                  className="primary medium"
                  label={model.isRetraining ? 'Retraining…' : 'Retrain template'}
                  disabled={!model.hasTrainingChanges || model.isRetraining || model.isReverting}
                  onClick={() => void model.retrain()}
                />
                {model.specialty.canRevert && (
                  <Button
                    className="secondary medium"
                    label={model.isReverting ? 'Reverting…' : 'Revert previous prompt'}
                    disabled={model.isRetraining || model.isReverting}
                    onClick={() => void model.revert()}
                  />
                )}
              </div>
            </div>

            <div className="drawer-section">
              <Button
                className="link link-red small custom-specialty-delete"
                label={model.isArchiving ? 'Deleting…' : 'Delete template'}
                disabled={model.isArchiving || model.isRetraining || model.isReverting}
                onClick={() => model.teamId && model.specialty && model.confirmArchive(model.teamId, model.specialty)}
              />
            </div>

            {model.specialty.generatedPrompt && (
              <div className="drawer-section">
                <h4 className="drawer-section-title">Generated Prompt</h4>
                <div className="drawer-prompt-container">
                  <pre className="drawer-prompt-content">
                    {model.isPromptExpanded
                      ? model.specialty.generatedPrompt
                      : model.truncatedPrompt}
                  </pre>
                  {model.specialty.generatedPrompt.length > 300 && (
                    <button
                      className="drawer-prompt-toggle"
                      onClick={() => model.togglePromptExpanded()}>
                      {model.isPromptExpanded ? 'Show less' : 'Show full prompt'}
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Window>
  );
});
